import { CALENDAR_MAX_WINDOW_DAYS, CALENDAR_TIMEZONE } from '@/types/shared';
import type { CalendarEvent } from '@/types/shared';

/**
 * Pure Cairo calendar-date + month-grid-window helpers for the `/calendar` page (T107, built
 * against the frozen T103 contract). `apps/api`'s `cairo-date.util.ts` isn't reachable from the
 * web app (separate package, server-only), so this is the "small local util" T107's brief calls
 * for — kept consistent with that module's exact math (Saturday-first week, UTC-midnight string
 * arithmetic once a Cairo date string is already in hand) so the two can never disagree on what a
 * "week"/"month grid" means, even though they can't literally share code across the FE/BE
 * package boundary.
 *
 * IMPORTANT: `CalendarEvent.date` is a SERVER-RESOLVED Cairo day bucket (T103 binding rule) — this
 * module only ever buckets/sorts by that string, it never re-derives a day boundary from an ISO
 * instant itself (the one exception, `cairoDateString`, is used solely to find "today" for the
 * initial month / the "Today" nav button, not to bucket any event).
 */

/** Cairo-local calendar date (`YYYY-MM-DD`) for the given instant (defaults to now) — used only to
 * resolve "today" (initial month, the Today button, today-cell highlighting), never to bucket a
 * server-returned event (those already carry their own resolved `date`). */
export function cairoDateString(instant: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CALENDAR_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/** `dateStr` (`YYYY-MM-DD`) shifted by `days` (may be negative) — pure calendar-date arithmetic,
 * no timezone conversion (the string never carries a time component). */
export function addDaysToDateString(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The `YYYY-MM` month key containing a Cairo `YYYY-MM-DD` date string. */
export function monthKeyFromDateString(dateStr: string): string {
  return dateStr.slice(0, 7);
}

const MONTH_KEY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Well-formed `YYYY-MM` check — guards the `?month=` URL param against a stale bookmark, a typo,
 * or a manually-edited query string before it ever reaches `computeMonthGridWindow`. */
export function isValidMonthKey(value: string | null | undefined): value is string {
  return Boolean(value && MONTH_KEY_PATTERN.test(value));
}

/** `monthKey` shifted by `delta` whole months (may be negative) — Prev/Next month nav. */
export function shiftMonthKey(monthKey: string, delta: number): string {
  const [year, month] = monthKey.split('-').map(Number);
  const totalMonths = year * 12 + (month - 1) + delta;
  const newYear = Math.floor(totalMonths / 12);
  const newMonth = (totalMonths % 12) + 1;
  return `${newYear}-${String(newMonth).padStart(2, '0')}`;
}

function utcDayOfWeek(dateStr: string): number {
  // 0=Sun..6=Sat
  return new Date(`${dateStr}T00:00:00.000Z`).getUTCDay();
}

export interface CalendarGridWindow {
  /** Inclusive Cairo lower bound (`YYYY-MM-DD`) — the Saturday on/before the 1st of the month. */
  from: string;
  /** Inclusive Cairo upper bound (`YYYY-MM-DD`) — the Friday on/after the last day of the month. */
  to: string;
  /** `from..to` chunked into Saturday-first weeks of 7 date strings each (5 or 6 weeks). */
  weeks: string[][];
}

/**
 * T103 R-WIN-3 / CalendarQueryParams: the visible Saturday-first month-grid window for `monthKey`
 * (`YYYY-MM`) — spills a few prev/next-month days so the grid always shows whole weeks. The span
 * is always a multiple of 7 (35 or 42 days) by construction, comfortably within
 * `CALENDAR_MAX_WINDOW_DAYS` (42) — the guard below is defensive only, in case the alignment math
 * is ever changed without noticing it can no longer only ever produce 35/42.
 */
export function computeMonthGridWindow(monthKey: string): CalendarGridWindow {
  const [year, month] = monthKey.split('-');
  const firstOfMonth = `${year}-${month}-01`;
  const lastDayNum = new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
  const lastOfMonth = `${year}-${month}-${String(lastDayNum).padStart(2, '0')}`;

  const firstDow = utcDayOfWeek(firstOfMonth);
  const daysSinceSaturday = (firstDow + 1) % 7;
  const from = addDaysToDateString(firstOfMonth, -daysSinceSaturday);

  const lastDow = utcDayOfWeek(lastOfMonth);
  const daysUntilFriday = (5 - lastDow + 7) % 7;
  const to = addDaysToDateString(lastOfMonth, daysUntilFriday);

  const days: string[] = [];
  for (let cursor = from; cursor <= to; cursor = addDaysToDateString(cursor, 1)) {
    days.push(cursor);
  }

  if (days.length > CALENDAR_MAX_WINDOW_DAYS) {
    throw new Error(
      `computeMonthGridWindow: grid window (${days.length} days) exceeds CALENDAR_MAX_WINDOW_DAYS`,
    );
  }

  const weeks: string[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return { from, to, weeks };
}

/**
 * Buckets events by their server-resolved `date` field — the T103 binding rule ("the FE never
 * recomputes a day boundary"). A day with no events simply has no map entry.
 */
export function bucketEventsByDate(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const bucket = map.get(event.date);
    if (bucket) {
      bucket.push(event);
    } else {
      map.set(event.date, [event]);
    }
  }
  return map;
}

/**
 * T104 §2.5 sort order within a day cell (and the day-detail popover, same order): timed events
 * (meetings) first, ascending by `startsAt`; then all-day events, exams before payments.
 */
export function sortDayEvents(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((a, b) => {
    if (a.type === 'meeting' && b.type === 'meeting') {
      return a.startsAt.localeCompare(b.startsAt);
    }
    if (a.type === 'meeting') return -1;
    if (b.type === 'meeting') return 1;
    if (a.type === b.type) return 0;
    return a.type === 'exam' ? -1 : 1;
  });
}
